"use client";

import React from "react";
import { Database, Search, LayoutTemplate, Maximize, TrendingUp } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  id: 1,
  year: "Phase 01",
  title: "Onboarding & Metrc Sync",
  description: "Establish a rock-solid compliance foundation by synchronizing your inventory and sales data directly with Metrc's regulatory framework.",
  metric: "100% Sync Accuracy",
  icon: Database
}, {
  id: 2,
  year: "Phase 02",
  title: "Single Location Excellence",
  description: "Master your retail operations with our high-performance iOS POS and unified CRM, designed to streamline customer flow and loyalty.",
  metric: "25% Higher LTV",
  icon: LayoutTemplate
}, {
  id: 3,
  year: "Phase 03",
  title: "Multi-State Expansion",
  description: "Scale across borders with confidence using automated document workflows and centralized oversight for complex multi-license operations.",
  metric: "Unlimited Scale",
  icon: Maximize
}, {
  id: 4,
  year: "Phase 04",
  title: "AI-Driven Optimization",
  description: "Harness the power of Salesforce-integrated AI to predict inventory needs, automate engagement, and optimize vertical profitability.",
  metric: "Predictive Insights",
  icon: TrendingUp
}];
function TimelineNode({
  item,
  index,
  isLast
}: {
  item: typeof milestones[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  const Icon = item.icon;
  return <div data-section-id="137111" ref={ref} className="relative flex flex-col md:flex-row items-center justify-between w-full mb-16 last:mb-0" data-index={index}>
      {}
      {!isLast && <div className="absolute left-6 md:left-1/2 top-12 bottom-[-4rem] w-1 -translate-x-1/2 bg-[#134B75]/20 overflow-hidden rounded-full">
           <div className={`w-full bg-gradient-to-b from-[#1A5A8E] to-[#4092C7] transition-all duration-1000 ease-out origin-top ${isVisible ? "h-full" : "h-0"}`} />
        </div>}

      {}
      <div className="absolute left-6 md:left-1/2 top-0 -translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full border-2 border-[#134B75] bg-white z-10 transition-colors duration-700 shadow-sm">
         <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-700 ${isVisible ? "bg-gradient-to-br from-[#1A5A8E] to-[#4092C7] text-white scale-100 ring-4 ring-[#1A5A8E]/20" : "bg-slate-100 text-slate-400 scale-75"}`}>
            <Icon className="w-4 h-4" />
         </div>
      </div>

      {}
      <div className={`w-full md:w-[calc(50%-3rem)] pl-16 md:pl-0 ${isEven ? "md:pr-12" : "md:pl-12 md:ml-auto"}`}>
         <Card className={`transition-all duration-700 transform border-[#134B75]/10 hover:border-[#1A5A8E]/40 shadow-xl shadow-slate-200/50 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <CardHeader>
               <div className={`font-mono text-sm font-bold tracking-widest mb-1 transition-colors duration-700 ${isVisible ? "text-[#1A5A8E]" : "text-slate-400"}`}>
                 {item.year}
               </div>
               <CardTitle className="text-2xl font-bold tracking-tight text-[#0F172A]">{item.title}</CardTitle>
            </CardHeader>
            <CardContent>
               <p className="text-slate-600 mb-4 leading-relaxed font-normal">
                 {item.description}
               </p>
               <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border transition-colors duration-700 ${isVisible ? "border-[#1A5A8E]/30 bg-[#1A5A8E]/5 text-[#1A5A8E]" : "border-slate-200 bg-slate-50 text-slate-400"}`}>
                  <TrendingUp className="w-3.5 h-3.5" />
                  {item.metric}
               </div>
            </CardContent>
         </Card>
      </div>
    </div>;
}
export default function GrowthJourney() {
  return <section id="compliant-scaling-milestones" className="bg-[#F8FAFC] text-[#0F172A] py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-gradient-to-b from-[#1A5A8E]/5 to-transparent blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 max-w-5xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[#0F172A] tracking-tighter">
            The <span className="bg-gradient-to-r from-[#1A5A8E] to-[#4092C7] bg-clip-text text-transparent">Cannabis Cloud</span> Roadmap
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed font-medium">
            From initial compliance synchronization to advanced AI-powered multi-state operations, discover how we empower dispensaries to scale with absolute confidence.
          </p>
        </div>

        <div className="relative">
          {milestones.map((item, index) => <TimelineNode key={item.id} item={item} index={index} isLast={index === milestones.length - 1} />)}
        </div>
      </div>
    </section>;
}